import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    signRequestId: String,
    locale: String,
    signers: Array,
    csrfToken: String,
    documents: Array
  }

  static targets = ["signerList", "pdfContainer", "fieldOverlay", "tool", "guidelines", "documentList", "currentDocumentName"]

  // Default field sizes (as percentage of page)
  static fieldSizes = {
    signature: { w: 0.2, h: 0.05 },
    initials: { w: 0.1, h: 0.04 },
    date: { w: 0.15, h: 0.03 }
  }

  // Minimum field sizes
  static minSizes = { w: 0.05, h: 0.02 }

  // Snap threshold (percentage)
  static snapThreshold = 0.01

  connect() {
    this.selectedSigner = null
    this.selectedTool = null
    this.fields = []
    this.currentPage = 1
    this.canvasWidth = 0
    this.canvasHeight = 0
    this.currentDocumentIndex = 0

    // Drag state
    this.dragging = null
    this.dragStartX = 0
    this.dragStartY = 0
    this.dragFieldStartX = 0
    this.dragFieldStartY = 0

    // Resize state
    this.resizing = null
    this.resizeHandle = null
    this.resizeStartX = 0
    this.resizeStartY = 0
    this.resizeFieldStart = null

    // Snap guidelines
    this.activeGuidelines = []

    // Bind handlers
    this.handleMouseMove = this.onMouseMove.bind(this)
    this.handleMouseUp = this.onMouseUp.bind(this)

    this.renderSigners()
    this.loadFields()
    this.updateDocumentSelector()

    // Listen for page rendered events from pdf-viewer controller
    this.element.addEventListener("pdf-viewer:pageRendered", this.handlePageRendered.bind(this))
  }

  disconnect() {
    this.element.removeEventListener("pdf-viewer:pageRendered", this.handlePageRendered.bind(this))
    document.removeEventListener("mousemove", this.handleMouseMove)
    document.removeEventListener("mouseup", this.handleMouseUp)
  }

  // Multi-document support
  get currentDocument() {
    if (this.documentsValue.length === 0) return null
    return this.documentsValue[this.currentDocumentIndex]
  }

  get currentDocumentId() {
    return this.currentDocument?.id || null
  }

  selectDocument(event) {
    const index = parseInt(event.currentTarget.dataset.documentIndex, 10)
    if (index === this.currentDocumentIndex) return

    this.currentDocumentIndex = index
    this.updateDocumentSelector()

    // Switch PDF in viewer
    const pdfController = this.application.getControllerForElementAndIdentifier(
      this.element, "pdf-viewer"
    )
    if (pdfController && this.currentDocument) {
      pdfController.switchDocument(this.currentDocument.url)
    }
  }

  updateDocumentSelector() {
    if (!this.hasDocumentListTarget) return

    const buttons = this.documentListTarget.querySelectorAll("button")
    buttons.forEach((btn, index) => {
      if (index === this.currentDocumentIndex) {
        btn.classList.remove("btn-outline-secondary")
        btn.classList.add("btn-primary")
      } else {
        btn.classList.remove("btn-primary")
        btn.classList.add("btn-outline-secondary")
      }
    })

    if (this.hasCurrentDocumentNameTarget && this.currentDocument) {
      this.currentDocumentNameTarget.textContent = `${this.currentDocument.filename}`
    }
  }

  handlePageRendered(event) {
    this.currentPage = event.detail.page
    this.canvasWidth = event.detail.width
    this.canvasHeight = event.detail.height
    this.renderFields()
  }

  renderSigners() {
    if (!this.hasSignerListTarget) return

    this.signerListTarget.innerHTML = this.signersValue.map((signer, index) => {
      const isSelected = this.selectedSigner?.id === signer.id
      const selectedStyle = isSelected
        ? "border: 2px solid #212529; box-shadow: 0 0 0 2px rgba(33, 37, 41, 0.25);"
        : ""

      return `
        <button type="button"
                class="btn btn-sm w-100 text-start btn-outline-secondary"
                style="${selectedStyle}"
                data-action="click->field-placement#selectSigner"
                data-signer-id="${signer.id}"
                data-signer-index="${index}">
          <span class="d-inline-block rounded-circle me-2" style="width: 12px; height: 12px; background-color: ${signer.color_hex}; border: 1px solid rgba(0,0,0,0.2);"></span>
          ${this.escapeHtml(signer.name)}
        </button>
      `
    }).join("")

    if (!this.selectedSigner && this.signersValue.length > 0) {
      this.selectedSigner = this.signersValue[0]
      this.renderSigners()
    }
  }

  selectSigner(event) {
    const index = parseInt(event.currentTarget.dataset.signerIndex, 10)
    this.selectedSigner = this.signersValue[index]
    this.renderSigners()
  }

  selectTool(event) {
    const fieldType = event.currentTarget.dataset.fieldType
    this.selectedTool = fieldType

    this.toolTargets.forEach(btn => {
      if (btn.dataset.fieldType === fieldType) {
        btn.classList.remove("btn-outline-secondary")
        btn.classList.add("btn-primary")
      } else {
        btn.classList.remove("btn-primary")
        btn.classList.add("btn-outline-secondary")
      }
    })
  }

  async placeField(event) {
    if (this.dragging || this.resizing) return
    if (!this.selectedSigner || !this.selectedTool) return
    if (this.canvasWidth === 0 || this.canvasHeight === 0) return

    const canvas = event.currentTarget
    const rect = canvas.getBoundingClientRect()

    const clickX = (event.clientX - rect.left) / rect.width
    const clickY = (event.clientY - rect.top) / rect.height

    const size = this.constructor.fieldSizes[this.selectedTool]

    const x = Math.max(0, Math.min(clickX - size.w / 2, 1 - size.w))
    const y = Math.max(0, Math.min(clickY - size.h / 2, 1 - size.h))

    const fieldData = {
      field: {
        signer_id: this.selectedSigner.id,
        page: this.currentPage,
        field_type: this.selectedTool,
        x: x,
        y: y,
        w: size.w,
        h: size.h,
        required: true
      }
    }

    // Include document ID if using multi-doc
    if (this.currentDocumentId) {
      fieldData.field.sign_request_document_id = this.currentDocumentId
    }

    try {
      const response = await fetch(`/${this.localeValue}/sign_requests/${this.signRequestIdValue}/fields`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfTokenValue
        },
        body: JSON.stringify(fieldData)
      })

      if (response.ok) {
        const field = await response.json()
        this.fields.push(field)
        this.renderFields()
      } else {
        const error = await response.json()
        console.error("Failed to create field:", error)
      }
    } catch (error) {
      console.error("Error creating field:", error)
    }
  }

  startDrag(event) {
    if (event.target.closest("button") || event.target.closest("[data-resize-handle]")) return

    event.preventDefault()
    event.stopPropagation()

    const fieldId = event.currentTarget.dataset.fieldId
    const field = this.fields.find(f => f.id === fieldId)
    if (!field) return

    this.dragging = field
    this.dragStartX = event.clientX
    this.dragStartY = event.clientY
    this.dragFieldStartX = field.x
    this.dragFieldStartY = field.y

    event.currentTarget.style.opacity = "0.7"
    event.currentTarget.style.cursor = "grabbing"

    document.addEventListener("mousemove", this.handleMouseMove)
    document.addEventListener("mouseup", this.handleMouseUp)
  }

  startResize(event) {
    event.preventDefault()
    event.stopPropagation()

    const handle = event.currentTarget.dataset.resizeHandle
    const fieldEl = event.currentTarget.closest("[data-field-id]")
    const fieldId = fieldEl.dataset.fieldId
    const field = this.fields.find(f => f.id === fieldId)
    if (!field) return

    this.resizing = field
    this.resizeHandle = handle
    this.resizeStartX = event.clientX
    this.resizeStartY = event.clientY
    this.resizeFieldStart = { x: field.x, y: field.y, w: field.w, h: field.h }

    document.addEventListener("mousemove", this.handleMouseMove)
    document.addEventListener("mouseup", this.handleMouseUp)
  }

  onMouseMove(event) {
    if (this.dragging) {
      this.handleDragMove(event)
    } else if (this.resizing) {
      this.handleResizeMove(event)
    }
  }

  handleDragMove(event) {
    if (!this.dragging || !this.hasFieldOverlayTarget) return

    const overlay = this.fieldOverlayTarget
    const rect = overlay.getBoundingClientRect()

    const deltaX = (event.clientX - this.dragStartX) / rect.width
    const deltaY = (event.clientY - this.dragStartY) / rect.height

    let newX = Math.max(0, Math.min(this.dragFieldStartX + deltaX, 1 - this.dragging.w))
    let newY = Math.max(0, Math.min(this.dragFieldStartY + deltaY, 1 - this.dragging.h))

    // Apply snapping
    const snapped = this.applySnapping(newX, newY, this.dragging.w, this.dragging.h, this.dragging.id)
    newX = snapped.x
    newY = snapped.y

    this.dragging.x = newX
    this.dragging.y = newY

    this.renderFields()
    this.renderGuidelines(snapped.guidelines)
  }

  handleResizeMove(event) {
    if (!this.resizing || !this.hasFieldOverlayTarget) return

    const overlay = this.fieldOverlayTarget
    const rect = overlay.getBoundingClientRect()

    const deltaX = (event.clientX - this.resizeStartX) / rect.width
    const deltaY = (event.clientY - this.resizeStartY) / rect.height

    const start = this.resizeFieldStart
    const minW = this.constructor.minSizes.w
    const minH = this.constructor.minSizes.h

    let newX = this.resizing.x
    let newY = this.resizing.y
    let newW = this.resizing.w
    let newH = this.resizing.h

    // Handle different resize handles
    switch (this.resizeHandle) {
      case "se": // Bottom-right
        newW = Math.max(minW, Math.min(start.w + deltaX, 1 - start.x))
        newH = Math.max(minH, Math.min(start.h + deltaY, 1 - start.y))
        break
      case "sw": // Bottom-left
        newW = Math.max(minW, start.w - deltaX)
        newX = Math.max(0, Math.min(start.x + deltaX, start.x + start.w - minW))
        newH = Math.max(minH, Math.min(start.h + deltaY, 1 - start.y))
        break
      case "ne": // Top-right
        newW = Math.max(minW, Math.min(start.w + deltaX, 1 - start.x))
        newH = Math.max(minH, start.h - deltaY)
        newY = Math.max(0, Math.min(start.y + deltaY, start.y + start.h - minH))
        break
      case "nw": // Top-left
        newW = Math.max(minW, start.w - deltaX)
        newX = Math.max(0, Math.min(start.x + deltaX, start.x + start.w - minW))
        newH = Math.max(minH, start.h - deltaY)
        newY = Math.max(0, Math.min(start.y + deltaY, start.y + start.h - minH))
        break
      case "e": // Right edge
        newW = Math.max(minW, Math.min(start.w + deltaX, 1 - start.x))
        break
      case "w": // Left edge
        newW = Math.max(minW, start.w - deltaX)
        newX = Math.max(0, Math.min(start.x + deltaX, start.x + start.w - minW))
        break
      case "s": // Bottom edge
        newH = Math.max(minH, Math.min(start.h + deltaY, 1 - start.y))
        break
      case "n": // Top edge
        newH = Math.max(minH, start.h - deltaY)
        newY = Math.max(0, Math.min(start.y + deltaY, start.y + start.h - minH))
        break
    }

    // Apply snapping for edges being resized
    const snapped = this.applyResizeSnapping(newX, newY, newW, newH, this.resizing.id, this.resizeHandle)

    this.resizing.x = snapped.x
    this.resizing.y = snapped.y
    this.resizing.w = snapped.w
    this.resizing.h = snapped.h

    this.renderFields()
    this.renderGuidelines(snapped.guidelines)
  }

  applySnapping(x, y, w, h, excludeId) {
    const threshold = this.constructor.snapThreshold
    const guidelines = []
    const otherFields = this.getPageFields().filter(f => f.id !== excludeId)

    let snappedX = x
    let snappedY = y

    // Field edges
    const left = x
    const right = x + w
    const top = y
    const bottom = y + h
    const centerX = x + w / 2
    const centerY = y + h / 2

    for (const other of otherFields) {
      const oLeft = other.x
      const oRight = other.x + other.w
      const oTop = other.y
      const oBottom = other.y + other.h
      const oCenterX = other.x + other.w / 2
      const oCenterY = other.y + other.h / 2

      // Horizontal snapping
      if (Math.abs(left - oLeft) < threshold) {
        snappedX = oLeft
        guidelines.push({ type: "v", pos: oLeft })
      } else if (Math.abs(left - oRight) < threshold) {
        snappedX = oRight
        guidelines.push({ type: "v", pos: oRight })
      } else if (Math.abs(right - oLeft) < threshold) {
        snappedX = oLeft - w
        guidelines.push({ type: "v", pos: oLeft })
      } else if (Math.abs(right - oRight) < threshold) {
        snappedX = oRight - w
        guidelines.push({ type: "v", pos: oRight })
      } else if (Math.abs(centerX - oCenterX) < threshold) {
        snappedX = oCenterX - w / 2
        guidelines.push({ type: "v", pos: oCenterX })
      }

      // Vertical snapping
      if (Math.abs(top - oTop) < threshold) {
        snappedY = oTop
        guidelines.push({ type: "h", pos: oTop })
      } else if (Math.abs(top - oBottom) < threshold) {
        snappedY = oBottom
        guidelines.push({ type: "h", pos: oBottom })
      } else if (Math.abs(bottom - oTop) < threshold) {
        snappedY = oTop - h
        guidelines.push({ type: "h", pos: oTop })
      } else if (Math.abs(bottom - oBottom) < threshold) {
        snappedY = oBottom - h
        guidelines.push({ type: "h", pos: oBottom })
      } else if (Math.abs(centerY - oCenterY) < threshold) {
        snappedY = oCenterY - h / 2
        guidelines.push({ type: "h", pos: oCenterY })
      }
    }

    return { x: snappedX, y: snappedY, guidelines }
  }

  applyResizeSnapping(x, y, w, h, excludeId, handle) {
    const threshold = this.constructor.snapThreshold
    const guidelines = []
    const otherFields = this.getPageFields().filter(f => f.id !== excludeId)

    let snappedX = x
    let snappedY = y
    let snappedW = w
    let snappedH = h

    for (const other of otherFields) {
      const oLeft = other.x
      const oRight = other.x + other.w
      const oTop = other.y
      const oBottom = other.y + other.h

      // Snap right edge
      if (handle.includes("e")) {
        const right = x + w
        if (Math.abs(right - oLeft) < threshold) {
          snappedW = oLeft - x
          guidelines.push({ type: "v", pos: oLeft })
        } else if (Math.abs(right - oRight) < threshold) {
          snappedW = oRight - x
          guidelines.push({ type: "v", pos: oRight })
        }
      }

      // Snap left edge
      if (handle.includes("w")) {
        if (Math.abs(x - oLeft) < threshold) {
          snappedW = w + (x - oLeft)
          snappedX = oLeft
          guidelines.push({ type: "v", pos: oLeft })
        } else if (Math.abs(x - oRight) < threshold) {
          snappedW = w + (x - oRight)
          snappedX = oRight
          guidelines.push({ type: "v", pos: oRight })
        }
      }

      // Snap bottom edge
      if (handle.includes("s")) {
        const bottom = y + h
        if (Math.abs(bottom - oTop) < threshold) {
          snappedH = oTop - y
          guidelines.push({ type: "h", pos: oTop })
        } else if (Math.abs(bottom - oBottom) < threshold) {
          snappedH = oBottom - y
          guidelines.push({ type: "h", pos: oBottom })
        }
      }

      // Snap top edge
      if (handle.includes("n")) {
        if (Math.abs(y - oTop) < threshold) {
          snappedH = h + (y - oTop)
          snappedY = oTop
          guidelines.push({ type: "h", pos: oTop })
        } else if (Math.abs(y - oBottom) < threshold) {
          snappedH = h + (y - oBottom)
          snappedY = oBottom
          guidelines.push({ type: "h", pos: oBottom })
        }
      }
    }

    return { x: snappedX, y: snappedY, w: snappedW, h: snappedH, guidelines }
  }

  renderGuidelines(guidelines) {
    if (!this.hasGuidelinesTarget) return

    if (!guidelines || guidelines.length === 0) {
      this.guidelinesTarget.innerHTML = ""
      return
    }

    // Deduplicate guidelines
    const unique = []
    for (const g of guidelines) {
      if (!unique.find(u => u.type === g.type && Math.abs(u.pos - g.pos) < 0.001)) {
        unique.push(g)
      }
    }

    this.guidelinesTarget.innerHTML = unique.map(g => {
      if (g.type === "v") {
        return `<div class="position-absolute" style="left: ${g.pos * 100}%; top: 0; width: 1px; height: 100%; background-color: #0d6efd; pointer-events: none;"></div>`
      } else {
        return `<div class="position-absolute" style="left: 0; top: ${g.pos * 100}%; width: 100%; height: 1px; background-color: #0d6efd; pointer-events: none;"></div>`
      }
    }).join("")
  }

  async onMouseUp(event) {
    document.removeEventListener("mousemove", this.handleMouseMove)
    document.removeEventListener("mouseup", this.handleMouseUp)

    // Clear guidelines
    this.renderGuidelines([])

    if (this.dragging) {
      await this.finishDrag()
    } else if (this.resizing) {
      await this.finishResize()
    }
  }

  async finishDrag() {
    const field = this.dragging
    this.dragging = null

    try {
      const response = await fetch(`/${this.localeValue}/sign_requests/${this.signRequestIdValue}/fields/${field.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfTokenValue
        },
        body: JSON.stringify({
          field: { x: field.x, y: field.y }
        })
      })

      if (response.ok) {
        const updatedField = await response.json()
        const index = this.fields.findIndex(f => f.id === field.id)
        if (index !== -1) {
          this.fields[index] = updatedField
        }
      } else {
        console.error("Failed to update field position")
        this.loadFields()
      }
    } catch (error) {
      console.error("Error updating field:", error)
      this.loadFields()
    }

    this.renderFields()
  }

  async finishResize() {
    const field = this.resizing
    this.resizing = null
    this.resizeHandle = null
    this.resizeFieldStart = null

    try {
      const response = await fetch(`/${this.localeValue}/sign_requests/${this.signRequestIdValue}/fields/${field.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfTokenValue
        },
        body: JSON.stringify({
          field: { x: field.x, y: field.y, w: field.w, h: field.h }
        })
      })

      if (response.ok) {
        const updatedField = await response.json()
        const index = this.fields.findIndex(f => f.id === field.id)
        if (index !== -1) {
          this.fields[index] = updatedField
        }
      } else {
        console.error("Failed to update field size")
        this.loadFields()
      }
    } catch (error) {
      console.error("Error updating field:", error)
      this.loadFields()
    }

    this.renderFields()
  }

  async deleteField(event) {
    event.stopPropagation()
    event.preventDefault()
    const fieldId = event.currentTarget.dataset.fieldId

    try {
      const response = await fetch(`/${this.localeValue}/sign_requests/${this.signRequestIdValue}/fields/${fieldId}`, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": this.csrfTokenValue
        }
      })

      if (response.ok) {
        this.fields = this.fields.filter(f => f.id !== fieldId)
        this.renderFields()
      }
    } catch (error) {
      console.error("Error deleting field:", error)
    }
  }

  async loadFields() {
    try {
      const response = await fetch(`/${this.localeValue}/sign_requests/${this.signRequestIdValue}/fields`, {
        headers: {
          "Accept": "application/json"
        }
      })

      if (response.ok) {
        this.fields = await response.json()
        this.renderFields()
      }
    } catch (error) {
      console.error("Error loading fields:", error)
    }
  }

  // Get fields for current page and document
  getPageFields() {
    return this.fields.filter(f => {
      if (f.page !== this.currentPage) return false
      // If using multi-doc, filter by current document
      if (this.currentDocumentId && f.sign_request_document_id) {
        return f.sign_request_document_id === this.currentDocumentId
      }
      return true
    })
  }

  renderFields() {
    if (!this.hasFieldOverlayTarget) return

    const pageFields = this.getPageFields()

    this.fieldOverlayTarget.innerHTML = pageFields.map(field => `
      <div class="position-absolute d-flex align-items-center justify-content-center"
           style="left: ${field.x * 100}%; top: ${field.y * 100}%; width: ${field.w * 100}%; height: ${field.h * 100}%; background-color: ${field.signer_color_hex}40; border: 2px solid ${field.signer_color_hex}; pointer-events: auto; cursor: grab; user-select: none;"
           title="${field.field_type_label}"
           data-field-id="${field.id}"
           data-action="mousedown->field-placement#startDrag">
        <span class="small text-truncate px-1" style="color: ${field.signer_color_hex}; font-weight: 600; pointer-events: none;">
          ${field.field_type_label}
        </span>

        <!-- Delete button -->
        <button type="button"
                class="btn btn-sm position-absolute top-0 end-0 p-0 lh-1"
                style="transform: translate(50%, -50%); background-color: ${field.signer_color_hex}; color: white; width: 18px; height: 18px; border-radius: 50%; font-size: 10px;"
                data-action="click->field-placement#deleteField"
                data-field-id="${field.id}">
          &times;
        </button>

        <!-- Resize handles (ne omitted - conflicts with delete button) -->
        <div data-resize-handle="nw" data-action="mousedown->field-placement#startResize" class="position-absolute" style="top: -4px; left: -4px; width: 8px; height: 8px; background: white; border: 1px solid ${field.signer_color_hex}; cursor: nw-resize;"></div>
        <div data-resize-handle="sw" data-action="mousedown->field-placement#startResize" class="position-absolute" style="bottom: -4px; left: -4px; width: 8px; height: 8px; background: white; border: 1px solid ${field.signer_color_hex}; cursor: sw-resize;"></div>
        <div data-resize-handle="se" data-action="mousedown->field-placement#startResize" class="position-absolute" style="bottom: -4px; right: -4px; width: 8px; height: 8px; background: white; border: 1px solid ${field.signer_color_hex}; cursor: se-resize;"></div>

        <!-- Edge resize handles -->
        <div data-resize-handle="n" data-action="mousedown->field-placement#startResize" class="position-absolute" style="top: -3px; left: 20%; right: 20%; height: 6px; cursor: n-resize;"></div>
        <div data-resize-handle="s" data-action="mousedown->field-placement#startResize" class="position-absolute" style="bottom: -3px; left: 20%; right: 20%; height: 6px; cursor: s-resize;"></div>
        <div data-resize-handle="w" data-action="mousedown->field-placement#startResize" class="position-absolute" style="left: -3px; top: 20%; bottom: 20%; width: 6px; cursor: w-resize;"></div>
        <div data-resize-handle="e" data-action="mousedown->field-placement#startResize" class="position-absolute" style="right: -3px; top: 20%; bottom: 20%; width: 6px; cursor: e-resize;"></div>
      </div>
    `).join("")
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
